import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Lock, ShieldCheck, AlertOctagon, Mail, Crown } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * /invite/:token — public landing page for an emailed invite.
 *
 * Fetches /api/invites/{token}/preview to display whom this invite is
 * for and who sent it, then collects a password and POSTS to
 * /api/invites/accept. On success, the server returns a JWT and we
 * log the new user straight in.
 */
const InviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUserFromToken } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/invites/${token}/preview`);
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Invite link is invalid or expired.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { toast.error("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/invites/accept", { token, password: pw });
      const { access_token, user } = r.data || {};
      if (access_token) {
        // Log them in straight away
        try { await setUserFromToken?.(access_token, user); } catch (e) { console.warn("setUserFromToken", e); }
        localStorage.setItem("token", access_token);
        toast.success("Welcome! You're signed in.");
        navigate(user?.role === "admin" || user?.role === "superuser" ? "/dashboard" : "/dashboard", { replace: true });
      } else {
        toast.success("Account created. Please sign in.");
        navigate("/login", { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept invite.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian noise-bg text-slate-100 flex items-center justify-center px-4 py-12" data-testid="invite-accept-page">
      {/* CAPMARKETS-149: this page has no DashboardLayout, so no `.dashboard-shell`
          light-theme remap reaches it. `bg-surface-highlight` flips (cream in light),
          so every text colour below is a flipping brand token too — the fixed
          `text-white` / `text-amber-200` / `text-slate-200` it replaced measured
          1.08–1.15:1 on cream. Guarded by styles/outOfShellContrast.test.js. */}
      <Card className="w-full max-w-lg border-amber-400/30 bg-surface-highlight">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400/30 to-amber-600/10 border border-amber-400/40 flex items-center justify-center">
              <Crown className="text-gold-dark" size={18} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold-dark font-semibold">
                EchoSense Capital Club
              </p>
              <h1 className="text-xl font-serif text-cream">Activate Your Account</h1>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center text-platinum-dark py-6">
              <Loader2 className="animate-spin mr-2" size={16} /> Loading invite…
            </div>
          ) : err ? (
            <div data-testid="invite-error">
              <div className="rounded-lg border border-red-500/30 bg-red-500/[0.05] p-4 text-sm text-cream flex items-start gap-2">
                <AlertOctagon size={16} className="shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
              <Link to="/" className="block mt-4 text-center text-gold-dark hover:underline text-sm">
                Return to EchoSense Capital Club
              </Link>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-amber-400/20 bg-amber-400/[0.04] p-4 text-sm text-platinum mb-5 space-y-1">
                <p><Mail size={12} className="inline mr-1" /> <span className="text-gold-dark">{preview.email}</span></p>
                <p className="text-xs text-platinum-dark">
                  Role: <span className="capitalize text-cream">{preview.role}</span>
                </p>
                <p className="text-xs text-platinum-dark">
                  Invited by <span className="text-cream">{preview.inviter}</span>
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-platinum/80 text-sm">Choose a password</Label>
                  <Input
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 8 characters"
                    className="mt-1.5 bg-obsidian border-platinum/15 text-platinum"
                    data-testid="invite-pw-input"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <Label className="text-platinum/80 text-sm">Confirm password</Label>
                  <Input
                    type="password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    placeholder="Re-enter password"
                    className="mt-1.5 bg-obsidian border-platinum/15 text-platinum"
                    data-testid="invite-pw2-input"
                    autoComplete="new-password"
                  />
                </div>
                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="w-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold disabled:opacity-40 mt-2"
                  data-testid="invite-accept-btn"
                >
                  {submitting
                    ? <><Loader2 size={14} className="mr-2 animate-spin" /> Activating…</>
                    : <><ShieldCheck size={14} className="mr-2" /> Activate Account</>}
                </Button>
                <p className="text-[11px] text-platinum-dark leading-relaxed mt-3 flex items-start gap-2">
                  <Lock size={11} className="shrink-0 mt-0.5" />
                  Your password is set entirely by you. EchoSense staff cannot see it.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InviteAcceptPage;
